import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, BarChart3, Zap, Database, FileText, Search, Settings, Monitor } from "lucide-react";
import { AIInsight } from "@/types/seo";
interface AIInsightsProps {
  keyword: string;
  mainUrl: string;
  insights?: AIInsight[];
}
const BrainIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
    <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
    <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
    <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" />
    <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
    <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
    <path d="M19.938 10.5a4 4 0 0 1 .585.396" />
    <path d="M6 18a4 4 0 0 1-1.967-.516" />
    <path d="M19.967 17.484A4 4 0 0 1 18 18" />
  </svg>;
export const AIInsights = ({
  keyword,
  mainUrl,
  insights
}: AIInsightsProps) => {
  // Se não houver insights da API, não exibe o componente
  if (!insights || insights.length === 0) {
    return null;
  }
  
  const getCategoryIcon = (category: string, index: number) => {
    // Diferentes ícones baseados na categoria e posição para garantir variedade
    const contentIcons = [<FileText className="h-6 w-6 text-blue-600" />, <Search className="h-6 w-6 text-blue-600" />, <BarChart3 className="h-6 w-6 text-blue-600" />];
    const positioningIcons = [<Target className="h-6 w-6 text-amber-600" />, <Database className="h-6 w-6 text-amber-600" />];
    const technicalIcons = [<Zap className="h-6 w-6 text-green-600" />, <Settings className="h-6 w-6 text-green-600" />, <Monitor className="h-6 w-6 text-green-600" />];
    switch (category) {
      case "content":
        return contentIcons[index % contentIcons.length];
      case "positioning":
        return positioningIcons[index % positioningIcons.length];
      case "technical":
        return technicalIcons[index % technicalIcons.length];
      default:
        return <BrainIcon className="h-6 w-6" />;
    }
  };
  const getCategoryBackground = (category: string) => {
    switch (category) {
      case "positioning":
        return "bg-amber-50";
      case "content":
        return "bg-blue-50";
      case "technical":
        return "bg-green-50";
      default:
        return "bg-gray-50";
    }
  };
  const getActionEmoji = (index: number) => {
    const emojis = ["🎯", "🛠️", "⚙️", "⚡", "💡", "📱"];
    return emojis[index % emojis.length];
  };
  const getResultEmoji = (index: number) => {
    const emojis = ["📈", "🔍", "📊", "📋", "📚"];
    return emojis[index % emojis.length];
  };
  return <div className="mb-8 mt-32" data-section="ai-insights">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <BrainIcon className="h-7 w-7 text-blue-600" />
          <h2 className="text-2xl font-semibold text-slate-800">Insights com Inteligência Artificial</h2>
        </div>
        <p className="text-gray-600">
          Análise estratégica baseada em dados competitivos para "{keyword}" - {insights.length} ações recomendadas
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {insights.map((insight, index) => <div key={insight.id} className="bg-white rounded-2xl p-6 shadow-md relative overflow-hidden">
            {/* Header do card */}
            <div className="flex items-start gap-4 mb-5">
              <div className="w-8 h-8 bg-slate-800 text-white flex items-center justify-center font-semibold text-sm flex-shrink-0 rounded-full">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-slate-800 mb-2">
                  {insight.title}
                </h3>
              </div>
            </div>

            {/* Descrição */}
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              {insight.description}
            </p>

            {/* Ação Recomendada */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-red-600 font-semibold text-sm mb-2">
                {getActionEmoji(index)} Ação Recomendada:
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                {insight.action}
              </p>
            </div>

            {/* Resultado Esperado */}
            <div className="bg-green-50 rounded-xl p-4 border-l-4 border-green-500">
              <div className="flex items-center gap-2 text-green-700 font-semibold text-sm mb-2">
                {getResultEmoji(index)} Resultado Esperado:
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                {insight.expected_result}
              </p>
            </div>
          </div>)}
      </div>

      {/* Disclaimer */}
      <div className="mt-8">
        <p className="text-sm text-gray-700 text-center">
          A inteligência Artificial comete erros. Estes insights têm fins didáticos e ideativos. Sempre trabalhe com profissionais e agências capacitadas. Para contratar a Conversion,{" "}
          <a 
            href="https://www.conversion.com.br/contato/?utm_campaign=SEO%20Score&utm_source=tool&utm_medium=pagina&utm_term=conv&utm_content=botao-pagina-seo-score"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 underline font-medium"
          >
            fale conosco
          </a>
          .
        </p>
      </div>
    </div>;
};
